export interface CollectionResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  paging: Pagination;
}

interface Pagination {
  next: string | null;
  previous: string | null;
  first: string;
  last: string;
}

// these correspond to the LAYOUT_GRID and LAYOUT_PLAYER constants
// found in '~/objects/Album.php'
export const ALBUM_LAYOUTS = ['grid', 'player', 'live', 'playlist'] as const;

export type AlbumLayouts = typeof ALBUM_LAYOUTS[number];

// these correspond to the THEME_STANDARD and THEME_DARK constants
// found in '~/objects/Album.php'
export type AlbumThemes = 'standard' | 'dark';

export type StockClip = { clipId: number; name: string; pictures: Pictures };

export interface PictureSize {
  width: number;
  height: number;
  link: string;
}

export interface Pictures {
  active: boolean;
  sizes: PictureSize[];
  uri?: string;
  baseLink?: string;
}

export interface ClipUser {
  name: string;
  uri: string;
  link: string;
  pictures: Pictures;
}

export type LiveStatus = 'unavailable' | 'pending' | 'ready' | 'streaming' | 'done';
export type RecurringEvent = {
  album: {
    id: number;
    title: string;
  } | null;
  schedule: Schedule;
  chatEnabled: boolean;
  uri: string;
  streamPrivacy: {
    unlistedHash: string | null;
  };
};

export enum ContentRating {
  SAFE = 'safe',
  UNRATED = 'unrated',
  MATURE = 'explicit',
}
export interface Clip {
  displayType: 'CLIP';
  name: string;
  description: string;
  uri: string;
  user: ClipUser;
  duration: number;
  privacy: {
    view: string;
    download: boolean;
  };
  pictures: Pictures;
  isFree?: boolean;
  type: string;
  contentRatingClass: ContentRating;
  live?: {
    archivedTime: Date;
    status: LiveStatus;
    recurringEvent?: RecurringEvent;
    chat: {
      user: ClipUser;
      roomId: number;
      token: string;
    };
  };
}

export interface UserPlaceholder {
  name: string;
  pictures: PicturePlaceholder[];
}

export interface PicturePlaceholder {
  width: number;
  link: string;
}

export type ClipDataObject = ClipPlaceholder | Clip;

export interface ClipPlaceholder {
  displayType: 'CLIP_PLACEHOLDER';
  type: string;
  name: string;
  description: string;
  user: UserPlaceholder;
  picture?: PicturePlaceholder;
  pictures: Pictures;
  duration: number;
  isFree?: boolean;
  uri: string;
  contentRatingClass: ContentRating;
  privacy: {
    download: boolean;
    view: string;
  };
  live?: {
    archivedTime: Date;
    status: LiveStatus;
    recurringEvent?: RecurringEvent;
    chat: {
      user: ClipUser;
      roomId: string;
      token: string;
    };
  };
}

export interface VideoData {
  error?: string;
  errorCode?: number;
  items: Clip[];
  page: number;
  per_page: number;
  total: number;
  paging: {
    first: string;
    next?: string;
    previous?: string;
  };
}

export interface VimeoAlbumInfoResponse {
  allowShare: boolean;
  brandColor: string;
  customLogo: Pictures;
  description: string;
  embed: {
    html: string;
  };
  error?: string;
  hideNav: boolean;
  hideUpcoming: boolean;
  layout: AlbumLayouts;
  name: string;
  pictures: Pictures | Pictures[];
  privacy: {
    view: string;
  };
  sort: string;
  theme: AlbumThemes;
  user: {
    uri: string;
  };
  allowDownloads: boolean;
  allowContinuousPlay: boolean;
  webCustomLogo: boolean;
  domain: string;
  shareLink: string;
  hasChosenThumbnail: boolean;
  webBrandColor: boolean;
  metadata?: { connections?: { videos?: { total: number } } };
}

export interface ErrorNotification {
  error: Error;
  message: string;
}

export interface DailySchedule {
  type: 'single';
  startTime: string;
}

export interface WeeklySchedule {
  type: 'weekly';
  weekdays: number[];
  dailyTime: string;
}

export type Schedule = {
  rrule: string;
  startTime: string;
  endTime: string;
  timeZone: string;
};

export interface LiveChatStatusResponse {
  chatEnabled: boolean;
  roomId: number;
}

export type Team = {
  owner: {
    uri: string;
    name: string;
    account: string;
  };
  teamMembership: {
    permissionLevel: string;
    uri: string;
    role: string;
  };
  teamData: {
    teamShowcaseId: number;
    ownerId: number;
    teamName: string;
    pictures: {
      sizes: {
        link: string;
      }[];
    };
  };
  currentTeamSize: number;
  maxTeamSize: number;
  hasContentShared: boolean;
};

export const ScheduleFrequencyType = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  SINGLE: 'single',
} as const;
